import React from "react";
import { motion } from "framer-motion";
import { X, Check } from "lucide-react";

const ROWS = [
  { label: "Remembers yesterday's mistake", a: false, b: true },
  { label: "Knows your personal goals", a: false, b: true },
  { label: "Voice-native conversation", a: "partial", b: true },
  { label: "Adapts before you open the app", a: false, b: true },
  { label: "Spaced review built for you", a: false, b: true },
  { label: "Tracks confidence, not just accuracy", a: false, b: true },
];

const Cell = ({ value, positive }) => {
  if (value === true) {
    return (
      <div className="flex items-center gap-2">
        <span className={`w-5 h-5 rounded-full grid place-items-center ${positive ? "bg-aura-cyan/15 text-aura-cyan border border-aura-cyan/30" : "bg-panel/5 text-whisper"}`}>
          <Check size={12} strokeWidth={2} />
        </span>
        <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-ink/90">yes</span>
      </div>
    );
  }
  if (value === "partial") {
    return (
      <div className="flex items-center gap-2 text-whisper">
        <span className="w-5 h-5 rounded-full grid place-items-center bg-panel/5 border border-panel/10">
          <span className="w-1.5 h-1.5 bg-whisper rounded-full" />
        </span>
        <span className="font-mono text-[11px] tracking-[0.18em] uppercase">partial</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 text-whisper-muted">
      <span className="w-5 h-5 rounded-full grid place-items-center bg-panel/[0.03] border border-panel/10">
        <X size={11} strokeWidth={2} />
      </span>
      <span className="font-mono text-[11px] tracking-[0.18em] uppercase">no</span>
    </div>
  );
};

const Comparison = () => {
  return (
    <section className="relative py-28 px-6" data-testid="section-comparison">
      <div className="max-w-7xl mx-auto">
        <div className="section-label">Normal apps vs AURA</div>
        <h2 className="headline mt-4 text-4xl sm:text-5xl lg:text-[72px] max-w-3xl">
          The difference is <em>memory</em>.
        </h2>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="mt-12 grid lg:grid-cols-12 gap-4"
        >
          <div className="lg:col-span-4" />
          <div className="lg:col-span-4 glass rounded-3xl p-6 hairline">
            <div className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-whisper-muted">Normal apps</div>
            <div className="mt-2 font-display text-[24px] font-light tracking-tight text-ink/80">Static curriculum</div>
            <p className="mt-1 text-whisper text-[13px]">Same path for everyone.</p>
          </div>
          <div className="lg:col-span-4 rounded-3xl p-6 node-card edge-glow">
            <div className="relative z-10 font-mono text-[10.5px] tracking-[0.22em] uppercase text-aura-cyan">AURA</div>
            <div className="relative z-10 mt-2 font-display text-[24px] font-light tracking-tight text-ink">Living memory</div>
            <p className="relative z-10 mt-1 text-whisper text-[13px]">A path rewritten around you.</p>
          </div>

          {ROWS.map((r, i) => (
            <React.Fragment key={r.label}>
              <div className="lg:col-span-4 flex items-center gap-3 py-4 border-t border-panel/5 text-ink/90 text-[14.5px]">
                <span className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-whisper-muted w-8">{String(i + 1).padStart(2, "0")}</span>
                {r.label}
              </div>
              <div className="lg:col-span-4 py-4 border-t border-panel/5 px-4">
                <Cell value={r.a} />
              </div>
              <div className="lg:col-span-4 py-4 border-t border-aura-cyan/10 px-4 bg-aura-cyan/[0.02]">
                <Cell value={r.b} positive />
              </div>
            </React.Fragment>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Comparison;
